import { useState, useEffect } from 'react';
import { User, Users, Network, Target, Goal, Vote } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { OrgChartDisplay } from './OrgChartDisplay';
import { useLanguage } from '@/contexts/LanguageContext';

const iconMap: Record<string, any> = {
  Users,
  User,
  Network,
  Target,
  Goal,
  Vote
};

interface ProfileSectionData {
  id: string;
  title: string | { ar: string; en: string; };
  content: string | { ar: string; en: string; };
  icon: string;
  order: number;
  imageUrl?: string;
  orgData?: OrgNode[];
}

interface OrgNode {
  id: string;
  name: string;
  title: string;
  email: string;
  parentId: string | null;
  order: number;
}

type BilingualField = string | { ar: string; en: string };

interface BoardMember {
  id: string;
  name: BilingualField;
  title: BilingualField;
  credentials: BilingualField;
  image: string;
  order: number;
}

interface AssemblyMember {
  id: string;
  name: BilingualField;
  title: BilingualField;
  credentials: BilingualField;
  image: string;
  order: number;
}

interface CEOData {
  name: BilingualField;
  credentials: BilingualField;
  image: string;
  email?: string;
}

export function LandingProfile() {
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [profileSections, setProfileSections] = useState<ProfileSectionData[]>([]);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [assemblyMembers, setAssemblyMembers] = useState<AssemblyMember[]>([]);
  const [ceoData, setCeoData] = useState<CEOData | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  const localize = (field: BilingualField): string => {
    if (typeof field === 'string') return field;
    return field[language] || field.ar || field.en || '';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch profile sections
      const sectionsQuery = query(
        collection(db, 'landing_profile_sections'),
        orderBy('order', 'asc')
      );
      const sectionsSnapshot = await getDocs(sectionsQuery);
      const sectionsData = sectionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ProfileSectionData[];

      // Fetch board members
      const membersQuery = query(
        collection(db, 'landing_board_members'),
        orderBy('order', 'asc')
      );
      const membersSnapshot = await getDocs(membersQuery);
      const membersData = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BoardMember[];

      // Fetch assembly members
      const assemblyQuery = query(
        collection(db, 'landing_assembly_members'),
        orderBy('order', 'asc')
      );
      const assemblySnapshot = await getDocs(assemblyQuery);
      const assemblyData = assemblySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AssemblyMember[];

      // Fetch CEO data
      const ceoDoc = await getDocs(collection(db, 'landing_ceo'));
      if (!ceoDoc.empty) {
        const ceoDocData = ceoDoc.docs[0].data() as CEOData;
        setCeoData(ceoDocData);
      }

      setProfileSections(sectionsData);
      setBoardMembers(membersData);
      setAssemblyMembers(assemblyData);
    } catch (err) {
      console.error('Error fetching profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getContentForSection = (section: ProfileSectionData) => {
    // If this is the CEO section and we have data, show it
    if (section.id === 'manager' && ceoData) {
      return (
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="w-full md:w-[23%]">
            <img
              src={ceoData.image || 'https://via.placeholder.com/400'}
              alt={localize(ceoData.name)}
              className="w-full rounded-xl shadow-lg object-cover"
            />
          </div>
          <div className="flex-1">
            <h4 className="text-2xl font-bold text-[#004250] mb-4">{localize(ceoData.name)}</h4>
            {ceoData.email && (
              <p className="text-lg text-[#7dda55] font-medium mb-4">{ceoData.email}</p>
            )}
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{localize(ceoData.credentials)}</p>
          </div>
        </div>
      );
    }

    // If this is the strategy section, show structured components
    if (section.id === 'strategy') {
      const data = section as any;
      const localizeField = (field: any): string => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (typeof field === 'object') return field[language] || field.ar || field.en || '';
        return '';
      };
      return (
        <div className="space-y-8">
          {data.vision && (
            <div className="bg-white rounded-lg p-6 shadow-sm border-r-4 border-[#7dda55]">
              <h4 className="text-xl font-bold text-[#004250] mb-3">{t('landing.profile.vision')}</h4>
              <p className="text-gray-700 leading-relaxed">{localizeField(data.vision)}</p>
            </div>
          )}

          {data.mission && (
            <div className="bg-white rounded-lg p-6 shadow-sm border-r-4 border-[#7dda55]">
              <h4 className="text-xl font-bold text-[#004250] mb-3">{t('landing.profile.mission')}</h4>
              <p className="text-gray-700 leading-relaxed">{localizeField(data.mission)}</p>
            </div>
          )}

          {data.goals && (
            <div className="bg-white rounded-lg p-6 shadow-sm border-r-4 border-[#7dda55]">
              <h4 className="text-xl font-bold text-[#004250] mb-3">{t('landing.profile.goals')}</h4>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{localizeField(data.goals)}</p>
            </div>
          )}

          {/* Strategic Map */}
          {data.strategicMapImage && (
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="text-xl font-bold text-[#004250] mb-4">{t('landing.profile.strategicMap')}</h4>
              <img
                src={data.strategicMapImage}
                alt={t('landing.profile.strategicMap')}
                className="w-full h-auto rounded-lg shadow-md"
              />
            </div>
          )}
        </div>
      );
    }

    // If this is the board section and we have members, show them
    if (section.id === 'board' && boardMembers.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {boardMembers.map(member => (
            <div key={member.id} className="bg-white rounded-2xl border-t-4 border-[#7dda55] shadow-md overflow-hidden">
              <div className="flex flex-col items-center text-center p-6">
                <div className="w-full aspect-square mb-4 overflow-hidden rounded-xl">
                  <img
                    src={member.image || 'https://via.placeholder.com/300'}
                    alt={localize(member.name)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-lg font-bold text-[#004250] mb-2">{localize(member.name)}</h4>
                <p className="text-[#7dda55] font-medium text-sm mb-2">{localize(member.title)}</p>
                <p className="text-gray-600 text-xs leading-relaxed">{localize(member.credentials)}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // If this is the assembly section and we have members, show them
    if (section.id === 'assembly' && assemblyMembers.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {assemblyMembers.map(member => (
            <div key={member.id} className="bg-white rounded-2xl border-t-4 border-[#7dda55] shadow-md overflow-hidden">
              <div className="flex flex-col items-center text-center p-6">
                <div className="w-full aspect-square mb-4 overflow-hidden rounded-xl">
                  <img
                    src={member.image || 'https://via.placeholder.com/300'}
                    alt={localize(member.name)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-lg font-bold text-[#004250] mb-2">{localize(member.name)}</h4>
                <p className="text-[#7dda55] font-medium text-sm mb-2">{localize(member.title)}</p>
                <p className="text-gray-600 text-xs leading-relaxed">{localize(member.credentials)}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // If this is the structure section, show org chart
    if (section.id === 'structure') {
      if (section.orgData && section.orgData.length > 0) {
        return <OrgChartDisplay data={section.orgData} />;
      }
      
      return (
        <div className="text-center text-gray-500 py-8">
          {t('landing.profile.noOrgData')}
        </div>
      );
    }

    // Otherwise show the regular content
    const content = typeof section.content === 'string' 
      ? section.content 
      : (typeof section.content === 'object' ? (section.content[language] || section.content.ar || section.content.en || '') : '');
    
    return (
      <div className="text-gray-600 whitespace-pre-line leading-relaxed">
        {content}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#004250] mb-12">{t('landing.profile.aboutUs')}</h2>
          <div className="text-center text-gray-600">{t('landing.common.loading')}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {profileSections.map((section, index) => {
            const IconComponent = iconMap[section.icon] || User;
            // Safe rendering for title with proper language selection
            const title = typeof section.title === 'string' 
              ? section.title 
              : (typeof section.title === 'object' ? (section.title[language] || section.title.ar || section.title.en || '') : '');
            
            return (
              <div
                key={section.id}
                className={`bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer text-center ${
                  selectedSection === index ? 'ring-2 ring-[#7dda55] shadow-lg' : ''
                }`}
                onClick={() => setSelectedSection(selectedSection === index ? null : index)}
              >
                <IconComponent className="w-16 h-16 mx-auto mb-4 text-[#7dda55]" />
                <h3 className="text-xl font-bold text-[#004250]">{title}</h3>
              </div>
            );
          })}
        </div>

        {selectedSection !== null && (
          <div className="mt-8 bg-gray-50 rounded-lg p-8 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center gap-4 mb-6">
              {(() => {
                const IconComponent = iconMap[profileSections[selectedSection].icon] || User;
                return <IconComponent className="w-12 h-12 flex-shrink-0 text-[#7dda55]" />;
              })()}
              <h3 className="text-2xl font-bold text-[#004250]">
                {(() => {
                  const section = profileSections[selectedSection];
                  return typeof section.title === 'string' 
                    ? section.title 
                    : (typeof section.title === 'object' ? (section.title[language] || section.title.ar || section.title.en || '') : '');
                })()}
              </h3>
            </div>
            <div className="w-full">
              {getContentForSection(profileSections[selectedSection])}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

